import type { Component, JSX } from 'solid-js'
import { createEffect, createMemo, createSignal, createUniqueId, For, onCleanup, onMount, Show, splitProps } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Card from '~/components/Card'
import ColorPicker from '~/components/ColorPicker'
import {
  applyFontSize,
  applyLink,
  COLOR_SWATCHES,
  caretToEnd,
  closestTag,
  countText,
  currentBlock,
  currentFontFamily,
  currentFontSize,
  ensureStyleWithCss,
  exec,
  FONT_FAMILIES,
  FONT_SIZES,
  indent,
  insertCodeBlock,
  insertHtml,
  insertInlineCode,
  outdent,
  queryState,
  rangeInside,
  restoreRange,
  saveRange,
  unwrapListsFromParagraphs
} from '~/components/editor/commands'
import { ImageDialog, type ImageValue, LinkDialog, type LinkValue, MAX_IMAGE_BYTES } from '~/components/editor/EditorDialogs'
import { compressImage, formatBytes } from '~/components/editor/imageCompression'
import { useDictation } from '~/components/editor/useDictation'
import { useImageResize } from '~/components/editor/useImageResize'
import FieldMessage from '~/components/FieldMessage'
import { useFormContext } from '~/components/Form'
import Icon from '~/components/Icon'
import Select from '~/components/Select'
import Textarea from '~/components/Textarea'
import Tooltip from '~/components/Tooltip'
import { useToast } from '~/components/useToast'
import { useFieldError } from '~/hooks/useFieldError'
import type { EditorFormatStateType, EditorInputPropsType, HeadingOptionType } from '~/types/EditorInput'
import { sanitizeHtml } from '~/utils/sanitizeHtml'

const HEADING_OPTIONS: HeadingOptionType[] = [
  { value: 'p', title: 'Texto normal' },
  { value: 'h1', title: 'Título 1' },
  { value: 'h2', title: 'Título 2' },
  { value: 'h3', title: 'Título 3' },
  { value: 'h4', title: 'Título 4' },
  { value: 'h5', title: 'Título 5' },
  { value: 'h6', title: 'Título 6' }
]

/**
 * `minHeight` y `maxHeight` se usan de las dos formas en la aplicación: como
 * valor CSS (`"200px"`, `"40vh"`) y como clase de Tailwind (`"min-h-[100px]"`).
 * Estos dos ayudantes separan un caso del otro para aplicar cada uno donde toca.
 */
const cssLength = (value?: string): string | undefined => (value && /^-?\d+(\.\d+)?(px|rem|em|vh|vw|%)$/.test(value.trim()) ? value.trim() : undefined)

const cssClass = (value?: string): string => (value && !cssLength(value) ? value : '')

const EMPTY_FORMAT: EditorFormatStateType = {
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  superscript: false,
  subscript: false,
  unorderedList: false,
  orderedList: false,
  blockquote: false,
  link: false,
  align: 'left',
  block: 'p',
  fontSize: null,
  fontFamily: ''
}

/**
 * Editor de texto enriquecido.
 *
 * Trabaja sobre un `contentEditable` con `document.execCommand`: es la única
 * forma de dar formato respetando la selección sin traerse un motor de edición
 * completo, y aquí no caben dependencias nuevas (la librería se publica).
 *
 * Lo que sostiene el resultado:
 * - **El estado de la barra se lee de la selección** (`selectionchange`), así
 *   que los botones se ven activos cuando el cursor está sobre texto con ese
 *   formato. Antes eran mudos y no había forma de saber qué tenía aplicado.
 * - **El formato viaja en `style`**, permitido por la política de saneado de
 *   `core`. Con `styleWithCSS` el navegador escribe `<span style>` en vez de
 *   `<font>`, que el saneado descartaba.
 * - **Las imágenes se seleccionan y se redimensionan** con tiradores propios:
 *   `contentEditable` ya no los trae.
 */
const EditorInput: Component<EditorInputPropsType> = props => {
  const [local, others] = splitProps(props, ['value', 'onChange', 'placeholder', 'class', 'disabled', 'minHeight', 'maxHeight', 'dictationLang', 'name', 'error', 'helperText'])
  const toast = useToast()

  const [html, setHtml] = createSignal(local.value ?? '')
  const [isFullscreen, setIsFullscreen] = createSignal(false)
  const [isHtmlMode, setIsHtmlMode] = createSignal(false)
  const [format, setFormat] = createSignal<EditorFormatStateType>(EMPTY_FORMAT)
  const [linkOpen, setLinkOpen] = createSignal(false)
  const [linkInitial, setLinkInitial] = createSignal<LinkValue>({ text: '', href: '', newTab: true })
  const [editingLink, setEditingLink] = createSignal(false)
  const [imageOpen, setImageOpen] = createSignal(false)
  const [floating, setFloating] = createSignal<{ left: number; top: number } | null>(null)
  /**
   * Barra contraída: deja sólo lo esencial, como la cinta de Word. Arranca
   * plegada —una sola fila— y se despliega desde el botón del extremo derecho.
   */
  const [collapsed, setCollapsed] = createSignal(true)
  /** Último color usado en cada selector, para que el cuadrito lo muestre. */
  const [textColor, setTextColor] = createSignal('#000000')
  const [highlightColor, setHighlightColor] = createSignal('#fef08a')

  let editorRef: HTMLDivElement | undefined
  let floatingRef: HTMLDivElement | undefined
  let savedRange: Range | null = null
  /** Último HTML emitido: evita que el `value` de vuelta reescriba el editor y mueva el cursor. */
  let lastEmitted = ''

  const getEditor = () => editorRef

  const emit = () => {
    const content = editorRef?.innerHTML ?? ''
    lastEmitted = content
    setHtml(content)
    local.onChange?.(content)
  }

  const fieldId = createUniqueId()
  const formCtx = useFormContext()
  const resolvedError = useFieldError(
    () => local.name,
    () => local.error
  )

  /**
   * El contenido vive en el `contenteditable` y en una señal, no en un input,
   * así que el `<Form>` no puede escribirlo por el DOM: hay que dárselo al
   * editor para que lo vuelque y lo sanee.
   */
  let hiddenRef: HTMLInputElement | undefined
  onMount(() => {
    if (!local.name || !formCtx) return

    formCtx.registerField(local.name, {
      getValue: () => html(),
      setValue: value => {
        const incoming = value === undefined || value === null ? '' : String(value)
        if (!editorRef || incoming === lastEmitted) return
        const safe = sanitizeHtml(incoming)
        if (editorRef.innerHTML === safe) return
        editorRef.innerHTML = safe || '<p><br></p>'
        lastEmitted = editorRef.innerHTML
        setHtml(editorRef.innerHTML)
      }
    })

    onCleanup(() => {
      if (local.name) formCtx.unregisterField(local.name)
    })
  })

  // Un oculto no dispara eventos por sí solo: sin esto el `<Form>` no se entera
  // de lo que se escribe hasta el envío.
  let lastNotified: string | null = null
  createEffect(() => {
    const current = html()
    if (!local.name || !hiddenRef || current === lastNotified) return
    lastNotified = current
    hiddenRef.dispatchEvent(new Event('input', { bubbles: true }))
    hiddenRef.dispatchEvent(new Event('change', { bubbles: true }))
  })

  const images = useImageResize(getEditor, emit)

  const dictation = useDictation({
    lang: local.dictationLang,
    onText: text => {
      const limpio = text.trim()
      if (!limpio) return
      // Lo dictado es texto, no marcado: se escapa antes de insertarlo para que
      // decir "menor que" o deletrear una etiqueta no cree elementos.
      const escapado = limpio.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      restoreRange(editorRef, savedRange)
      // El espacio va como `&nbsp;`: un espacio normal al final del bloque lo
      // colapsa el navegador y las frases dictadas se pegaban entre sí.
      insertHtml(`${escapado}&nbsp;`)
      savedRange = saveRange(editorRef)
      emit()
    },
    onError: message => toast.addToast({ title: message, type: 'danger' })
  })

  // ── Estado de la barra a partir de la selección ─────────────────────────
  const readFormat = () => {
    if (!editorRef || isHtmlMode()) return
    if (!rangeInside(editorRef)) return

    const block = currentBlock(editorRef)
    const tag = block?.tagName.toLowerCase() ?? 'p'
    const align = block ? window.getComputedStyle(block).textAlign : 'left'

    setFormat({
      bold: queryState('bold'),
      italic: queryState('italic'),
      underline: queryState('underline'),
      strike: queryState('strikeThrough'),
      superscript: queryState('superscript'),
      subscript: queryState('subscript'),
      unorderedList: queryState('insertUnorderedList'),
      orderedList: queryState('insertOrderedList'),
      blockquote: Boolean(closestTag(editorRef, 'blockquote')),
      link: Boolean(closestTag(editorRef, 'a')),
      align: align === 'center' || align === 'right' || align === 'justify' ? align : 'left',
      block: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag) ? tag : 'p',
      fontSize: currentFontSize(editorRef),
      fontFamily: currentFontFamily(editorRef)
    })
  }

  /** Mini-barra sobre la selección, al estilo de Docs o Notion. */
  const updateFloating = () => {
    if (!editorRef || isHtmlMode()) return setFloating(null)
    const range = rangeInside(editorRef)
    const sel = window.getSelection()
    if (!range || !sel || sel.isCollapsed || images.selected()) return setFloating(null)

    const rect = range.getBoundingClientRect()
    if (!rect.width && !rect.height) return setFloating(null)
    const host = editorRef.getBoundingClientRect()

    // La barra se centra sobre la selección, pero sin salirse del editor: al
    // seleccionar texto pegado al borde izquierdo se quedaba cortada por fuera.
    const barWidth = floatingRef?.offsetWidth ?? 200
    const half = barWidth / 2
    const wanted = rect.left - host.left + rect.width / 2
    const maxLeft = Math.max(half + 8, host.width - half - 8)

    setFloating({
      left: Math.min(Math.max(wanted, half + 8), maxLeft),
      top: Math.max(0, rect.top - host.top + editorRef.scrollTop - 44)
    })
  }

  const onSelectionChange = () => {
    if (!editorRef) return
    const active = document.activeElement
    if (active !== editorRef && !editorRef.contains(active)) return
    savedRange = saveRange(editorRef)
    readFormat()
    updateFloating()
  }

  onMount(() => {
    ensureStyleWithCss()
    if (editorRef) {
      const initial = local.value ?? ''
      editorRef.innerHTML = initial ? sanitizeHtml(initial) : '<p><br></p>'
      lastEmitted = editorRef.innerHTML
      setHtml(editorRef.innerHTML)
    }
    document.addEventListener('selectionchange', onSelectionChange)
  })

  onCleanup(() => document.removeEventListener('selectionchange', onSelectionChange))

  // El contenido de fuera sólo se vuelca si es realmente distinto de lo último
  // emitido: si no, cada tecla reescribía el editor y el cursor saltaba al final.
  createEffect(() => {
    const incoming = local.value
    if (incoming === undefined || !editorRef) return
    if (incoming === lastEmitted) return
    const safe = sanitizeHtml(incoming)
    if (editorRef.innerHTML === safe) return

    const hadFocus = document.activeElement === editorRef
    editorRef.innerHTML = safe || '<p><br></p>'
    lastEmitted = editorRef.innerHTML
    setHtml(editorRef.innerHTML)
    if (hadFocus) caretToEnd(editorRef)
  })

  const isEmpty = createMemo(() => {
    const normalized = html()
      .replace(/<p><br\s*\/?><\/p>/gi, '')
      .replace(/<br\s*\/?>/gi, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/<[^>]+>/g, '')
      .trim()
    return normalized.length === 0
  })

  const counter = createMemo(() => countText(html()))

  // ── Acciones ────────────────────────────────────────────────────────────
  const run = (command: string, value?: string) => {
    editorRef?.focus()
    restoreRange(editorRef, savedRange)
    exec(command, value)
    // Convertir en lista un párrafo vacío deja `<p><ul>…</ul></p>` en Chrome.
    if (command === 'insertUnorderedList' || command === 'insertOrderedList') unwrapListsFromParagraphs(editorRef)
    emit()
    readFormat()
  }

  const applyColor = (color: string, kind: 'text' | 'highlight') => {
    editorRef?.focus()
    restoreRange(editorRef, savedRange)
    exec(kind === 'text' ? 'foreColor' : 'hiliteColor', color)
    emit()
  }

  const changeFontSize = (px: number) => {
    editorRef?.focus()
    restoreRange(editorRef, savedRange)
    applyFontSize(editorRef, px)
    emit()
    readFormat()
  }

  const changeFontFamily = (family: string) => {
    editorRef?.focus()
    restoreRange(editorRef, savedRange)
    exec('fontName', family || 'inherit')
    emit()
    readFormat()
  }

  const openLinkDialog = () => {
    savedRange = saveRange(editorRef)
    const anchor = closestTag(editorRef, 'a') as HTMLAnchorElement | null
    const selected = window.getSelection()?.toString() ?? ''
    setEditingLink(Boolean(anchor))
    setLinkInitial({
      text: anchor?.textContent ?? selected,
      href: anchor?.getAttribute('href') ?? '',
      newTab: anchor ? anchor.getAttribute('target') === '_blank' : true
    })
    setLinkOpen(true)
  }

  const submitLink = (value: LinkValue) => {
    setLinkOpen(false)
    editorRef?.focus()
    const anchor = editingLink() ? (closestTag(editorRef, 'a') as HTMLAnchorElement | null) : null
    if (anchor) {
      anchor.setAttribute('href', value.href)
      anchor.textContent = value.text
      if (value.newTab) {
        anchor.setAttribute('target', '_blank')
        anchor.setAttribute('rel', 'noopener noreferrer')
      } else {
        anchor.removeAttribute('target')
        anchor.removeAttribute('rel')
      }
    } else {
      restoreRange(editorRef, savedRange)
      applyLink(value.text, value.href, value.newTab)
    }
    emit()
  }

  const removeLink = () => {
    setLinkOpen(false)
    editorRef?.focus()
    restoreRange(editorRef, savedRange)
    exec('unlink')
    emit()
  }

  const insertImageValue = (value: ImageValue) => {
    setImageOpen(false)
    editorRef?.focus()
    restoreRange(editorRef, savedRange)
    const styles = [
      value.width ? `width: ${value.width}px` : '',
      value.align === 'center' ? 'display: block; margin-left: auto; margin-right: auto' : '',
      value.align === 'left' ? 'float: left; margin-right: 12px' : '',
      value.align === 'right' ? 'float: right; margin-left: 12px' : ''
    ]
      .filter(Boolean)
      .join('; ')
    const widthAttr = value.width ? ` width="${value.width}"` : ''
    insertHtml(`<img src="${value.src}" alt="${value.alt.replace(/"/g, '&quot;')}"${widthAttr} style="${styles}">`)
    emit()
  }

  /** Inserta un archivo de imagen (pegado o soltado) como data URI. */
  const insertImageFile = async (file: File) => {
    if (!file.type.startsWith('image/')) return
    if (file.size > MAX_IMAGE_BYTES) {
      toast.addToast({ title: `La imagen no puede superar ${formatBytes(MAX_IMAGE_BYTES)}`, type: 'warning' })
      return
    }

    try {
      // Pegada o soltada, la imagen pasa por la misma compresión que la del
      // diálogo: si no, una captura de pantalla mete medio megabyte de base64.
      const resultado = await compressImage(file)
      restoreRange(editorRef, savedRange)
      insertHtml(`<img src="${resultado.dataUrl}" alt="${file.name.replace(/"/g, '')}" style="max-width: 100%">`)
      emit()
      if (resultado.compressed) {
        toast.addToast({ title: `Imagen comprimida: ${formatBytes(resultado.originalBytes)} → ${formatBytes(resultado.bytes)}`, type: 'info' })
      }
    } catch (error) {
      toast.addToast({ title: error instanceof Error ? error.message : 'No pudimos insertar la imagen', type: 'danger' })
    }
  }

  const toggleHtmlMode = () => {
    const next = !isHtmlMode()
    if (next) {
      setHtml(sanitizeHtml(editorRef?.innerHTML ?? html()))
      setIsHtmlMode(true)
      images.select(null)
      setFloating(null)
    } else {
      setIsHtmlMode(false)
      queueMicrotask(() => {
        if (!editorRef) return
        editorRef.innerHTML = sanitizeHtml(html()) || '<p><br></p>'
        lastEmitted = editorRef.innerHTML
        editorRef.focus()
        caretToEnd(editorRef)
        emit()
      })
    }
  }

  // ── Eventos del área editable ───────────────────────────────────────────
  const handleKeyDown = (e: KeyboardEvent) => {
    if (isHtmlMode()) return

    // Atajos
    if (e.ctrlKey || e.metaKey) {
      const key = e.key.toLowerCase()
      if (key === 'k') {
        e.preventDefault()
        openLinkDialog()
        return
      }
      if (e.shiftKey && key === 'x') {
        e.preventDefault()
        run('strikeThrough')
        return
      }
      if (e.shiftKey && (key === '7' || key === '&')) {
        e.preventDefault()
        run('insertOrderedList')
        return
      }
      if (e.shiftKey && (key === '8' || key === '(')) {
        e.preventDefault()
        run('insertUnorderedList')
        return
      }
      // bold/italic/underline los resuelve el navegador; sólo hay que refrescar la barra.
      if (['b', 'i', 'u'].includes(key)) {
        queueMicrotask(() => {
          emit()
          readFormat()
        })
        return
      }
    }

    // Tab dentro de una lista = anidar. Es lo que hace que las listas
    // encadenadas funcionen como en cualquier procesador de texto.
    if (e.key === 'Tab') {
      e.preventDefault()
      if (e.shiftKey) outdent()
      else indent()
      emit()
      readFormat()
      return
    }

    // Enter (con o sin Shift) lo resuelve el navegador. Interceptarlo para
    // forzar `formatBlock <p>` convertía el bloque donde estabas —un título, una
    // cita— en párrafo normal, así que el texto perdía el formato al saltar de
    // línea; y dentro de una lista rompía el salto al punto siguiente.
  }

  const handlePaste = (e: ClipboardEvent) => {
    if (isHtmlMode()) return
    const data = e.clipboardData
    if (!data) return

    // Imagen del portapapeles: se incrusta como data URI.
    const file = Array.from(data.files ?? []).find(f => f.type.startsWith('image/'))
    if (file) {
      e.preventDefault()
      savedRange = saveRange(editorRef)
      insertImageFile(file)
      return
    }

    // HTML de otra página: entra saneado, nunca tal cual.
    const pastedHtml = data.getData('text/html')
    if (pastedHtml) {
      e.preventDefault()
      insertHtml(sanitizeHtml(pastedHtml))
      emit()
    }
  }

  const handleDrop = (e: DragEvent) => {
    const file = Array.from(e.dataTransfer?.files ?? []).find(f => f.type.startsWith('image/'))
    if (!file) return
    e.preventDefault()
    savedRange = saveRange(editorRef)
    insertImageFile(file)
  }

  const handleClick = (e: MouseEvent) => {
    const target = e.target as HTMLElement
    if (target?.tagName === 'IMG') {
      images.select(target as HTMLImageElement)
      setFloating(null)
    } else if (images.selected()) {
      images.select(null)
    }
  }

  // ── Piezas de la interfaz ───────────────────────────────────────────────
  interface ToolProps {
    icon: string
    label: string
    active?: boolean
    disabled?: boolean
    onClick: () => void
  }

  /**
   * Botón de la barra. La ayuda va con el `Tooltip` de la librería, no con el
   * `title` del navegador: el nativo tarda un segundo largo en salir y no sigue
   * el estilo del resto del panel. El `aria-label` se conserva aparte para los
   * lectores de pantalla.
   */
  const Tool = (p: ToolProps) => (
    <Tooltip content={p.label} placement="top">
      <button
        type="button"
        aria-label={p.label}
        aria-pressed={p.active ? 'true' : 'false'}
        disabled={p.disabled}
        onMouseDown={e => e.preventDefault()}
        onClick={p.onClick}
        class={twMerge(
          'inline-flex size-8 shrink-0 items-center justify-center rounded-md transition-colors',
          'text-foreground-subtle hover:bg-surface-muted hover:text-foreground',
          p.active ? 'bg-primary-soft text-primary hover:bg-primary-soft hover:text-primary' : '',
          p.disabled ? 'opacity-40 pointer-events-none' : ''
        )}
      >
        <Icon icon={p.icon} size={19} />
      </button>
    </Tooltip>
  )

  const Divider = () => <span class="mx-0.5 h-6 w-px shrink-0 bg-border" />

  /**
   * Selector de color de la barra: el `ColorPicker` de la librería, sin envolver
   * en otro modal. Metido dentro de uno se veían dos capas superpuestas (el
   * modal y el desplegable del propio selector), que es justo lo que sobraba.
   *
   * El color se aplica sobre el rango guardado, porque abrir el selector saca el
   * foco del editor y con él la selección.
   */
  const ColorTool = (p: { kind: 'text' | 'highlight'; label: string }) => (
    <Tooltip content={p.label} placement="top">
      <span
        class="w-11 shrink-0"
        role="group"
        aria-label={p.label}
        data-color-tool={p.kind}
        onMouseDown={() => {
          savedRange = saveRange(editorRef) ?? savedRange
        }}
      >
        <ColorPicker
          size="xs"
          value={p.kind === 'text' ? textColor() : highlightColor()}
          presets={[...COLOR_SWATCHES]}
          showAlpha={p.kind === 'highlight'}
          showInputs={false}
          class="[&_span:nth-child(2)]:hidden"
          onChange={color => {
            if (p.kind === 'text') setTextColor(color)
            else setHighlightColor(color)
            applyColor(color, p.kind)
          }}
        />
      </span>
    </Tooltip>
  )

  const Toolbar = () => (
    // Sin edición no hay formato que aplicar: la barra se apaga entera para no
    // ofrecer botones que no harían nada.
    <div class="shrink-0 border-b border-border bg-surface-muted/40 px-2 py-1.5">
      <div class="flex flex-wrap items-center gap-0.5">
        {/* Las herramientas de formato se apagan en modo HTML y en sólo lectura,
            pero NO los botones del extremo: con todo el bloque desactivado, el
            botón de "Volver al editor" dejaba de responder y no había forma de
            salir del HTML. */}
        <span class={twMerge('flex flex-wrap items-center gap-0.5', isHtmlMode() || local.disabled ? 'pointer-events-none opacity-60' : '')}>
          {/* Lo esencial se queda siempre a la vista; el resto se pliega con el
            botón del extremo derecho, como la cinta de Word. */}
          <Tool icon="material-symbols-light:undo" label="Deshacer (Ctrl+Z)" onClick={() => run('undo')} />
          <Tool icon="material-symbols-light:redo" label="Rehacer (Ctrl+Y)" onClick={() => run('redo')} />
          <Divider />

          <Select value={format().block} options={HEADING_OPTIONS} onChange={value => run('formatBlock', `<${String(value)}>`)} size="xs" class="w-36 flex-none" aria-label="Estilo de párrafo" />

          <Show when={!collapsed()}>
            <Select
              value={format().fontFamily}
              options={FONT_FAMILIES.map(f => ({ value: f.value, title: f.title }))}
              onChange={value => changeFontFamily(String(value ?? ''))}
              size="xs"
              class="w-32 flex-none"
              aria-label="Tipografía"
            />
            <Select
              value={format().fontSize ? String(format().fontSize) : ''}
              options={FONT_SIZES.map(size => ({ value: String(size), title: String(size) }))}
              onChange={value => changeFontSize(Number(value))}
              size="xs"
              class="w-20 flex-none"
              aria-label="Tamaño de letra"
            />
          </Show>
          <Divider />

          <Tool icon="material-symbols-light:format-bold" label="Negrita (Ctrl+B)" active={format().bold} onClick={() => run('bold')} />
          <Tool icon="material-symbols-light:format-italic" label="Cursiva (Ctrl+I)" active={format().italic} onClick={() => run('italic')} />
          <Tool icon="material-symbols-light:format-underlined" label="Subrayado (Ctrl+U)" active={format().underline} onClick={() => run('underline')} />

          <Show when={!collapsed()}>
            <Tool icon="material-symbols-light:format-strikethrough" label="Tachado (Ctrl+Shift+X)" active={format().strike} onClick={() => run('strikeThrough')} />
            <ColorTool kind="text" label="Color del texto" />
            <ColorTool kind="highlight" label="Color de resaltado" />
            <Tool icon="material-symbols-light:superscript" label="Superíndice" active={format().superscript} onClick={() => run('superscript')} />
            <Tool icon="material-symbols-light:subscript" label="Subíndice" active={format().subscript} onClick={() => run('subscript')} />
            <Divider />

            <Tool icon="material-symbols-light:format-align-left" label="Alinear a la izquierda" active={format().align === 'left'} onClick={() => run('justifyLeft')} />
            <Tool icon="material-symbols-light:format-align-center" label="Centrar" active={format().align === 'center'} onClick={() => run('justifyCenter')} />
            <Tool icon="material-symbols-light:format-align-right" label="Alinear a la derecha" active={format().align === 'right'} onClick={() => run('justifyRight')} />
            <Tool icon="material-symbols-light:format-align-justify" label="Justificar" active={format().align === 'justify'} onClick={() => run('justifyFull')} />
          </Show>
          <Divider />

          <Tool icon="material-symbols-light:format-list-bulleted" label="Lista con viñetas (Ctrl+Shift+8)" active={format().unorderedList} onClick={() => run('insertUnorderedList')} />
          <Tool icon="material-symbols-light:format-list-numbered" label="Lista numerada (Ctrl+Shift+7)" active={format().orderedList} onClick={() => run('insertOrderedList')} />
          <Tool icon="material-symbols-light:link" label="Enlace (Ctrl+K)" active={format().link} onClick={openLinkDialog} />

          <Show when={!collapsed()}>
            <Tool
              icon="material-symbols-light:format-indent-decrease"
              label="Reducir sangría (Shift+Tab)"
              onClick={() => {
                outdent()
                emit()
              }}
            />
            <Tool
              icon="material-symbols-light:format-indent-increase"
              label="Aumentar sangría (Tab)"
              onClick={() => {
                indent()
                emit()
              }}
            />
            <Divider />

            <Tool
              icon="material-symbols-light:image-outline"
              label="Imagen"
              onClick={() => {
                savedRange = saveRange(editorRef)
                setImageOpen(true)
              }}
            />
            <Tool icon="material-symbols-light:format-quote" label="Cita" active={format().blockquote} onClick={() => run('formatBlock', format().blockquote ? '<p>' : '<blockquote>')} />
            <Tool
              icon="material-symbols-light:code"
              label="Código en línea"
              onClick={() => {
                insertInlineCode()
                emit()
              }}
            />
            <Tool
              icon="material-symbols-light:code-blocks-outline"
              label="Bloque de código"
              onClick={() => {
                insertCodeBlock()
                emit()
              }}
            />
            <Tool icon="material-symbols-light:horizontal-rule" label="Línea horizontal" onClick={() => run('insertHorizontalRule')} />
            <Tool icon="material-symbols-light:format-clear" label="Limpiar formato" onClick={() => run('removeFormat')} />
          </Show>

          <Show when={dictation.supported}>
            <Divider />
            <Tool
              icon={dictation.listening() ? 'material-symbols-light:mic' : 'material-symbols-light:mic-outline'}
              label={dictation.listening() ? 'Detener dictado' : 'Dictar por voz'}
              active={dictation.listening()}
              onClick={() => {
                savedRange = saveRange(editorRef) ?? savedRange
                editorRef?.focus()
                dictation.toggle()
              }}
            />
          </Show>
        </span>

        {/* Vista de HTML, pantalla completa y plegado, pegados a la derecha. */}
        <span class="ml-auto flex items-center gap-0.5">
          <Tool icon={isHtmlMode() ? 'material-symbols-light:edit-outline' : 'material-symbols-light:html'} label={isHtmlMode() ? 'Volver al editor' : 'Ver el HTML'} onClick={toggleHtmlMode} />
          <Tool
            icon={isFullscreen() ? 'material-symbols-light:fullscreen-exit' : 'material-symbols-light:fullscreen'}
            label={isFullscreen() ? 'Salir de pantalla completa' : 'Pantalla completa'}
            onClick={() => setIsFullscreen(prev => !prev)}
          />
          <Tool
            icon={collapsed() ? 'material-symbols-light:keyboard-double-arrow-down' : 'material-symbols-light:keyboard-double-arrow-up'}
            label={collapsed() ? 'Mostrar todas las herramientas' : 'Contraer la barra'}
            active={collapsed()}
            onClick={() => setCollapsed(prev => !prev)}
          />
        </span>
      </div>
    </div>
  )

  /** Marco con tiradores sobre la imagen seleccionada. */
  const ImageOverlay = () => (
    <Show when={images.box()}>
      {box => (
        <>
          <div
            class="pointer-events-none absolute z-20 rounded-sm ring-2 ring-primary"
            style={{ left: `${box().left}px`, top: `${box().top}px`, width: `${box().width}px`, height: `${box().height}px` }}
          />
          <For each={['nw', 'ne', 'sw', 'se'] as const}>
            {handle => (
              <span
                role="presentation"
                class="absolute z-30 size-3 cursor-nwse-resize rounded-full border-2 border-white bg-primary shadow"
                style={{
                  left: `${box().left + (handle === 'ne' || handle === 'se' ? box().width : 0) - 6}px`,
                  top: `${box().top + (handle === 'sw' || handle === 'se' ? box().height : 0) - 6}px`,
                  cursor: handle === 'ne' || handle === 'sw' ? 'nesw-resize' : 'nwse-resize'
                }}
                onPointerDown={e => images.startResize(e, handle)}
              />
            )}
          </For>
          <div
            class="absolute z-30 flex items-center gap-0.5 rounded-lg border border-border bg-surface px-1 py-0.5 shadow-lg"
            style={{ left: `${box().left}px`, top: `${Math.max(0, box().top - 40)}px` }}
          >
            <Tool icon="material-symbols-light:align-horizontal-left" label="Alinear a la izquierda" onClick={() => images.setAlignment('left')} />
            <Tool icon="material-symbols-light:align-horizontal-center" label="Centrar" onClick={() => images.setAlignment('center')} />
            <Tool icon="material-symbols-light:align-horizontal-right" label="Alinear a la derecha" onClick={() => images.setAlignment('right')} />
            <Divider />
            <For each={[25, 50, 100]}>
              {percent => (
                <button
                  type="button"
                  class="rounded-md px-1.5 py-1 text-xs text-foreground-subtle hover:bg-surface-muted hover:text-foreground"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => images.setRelativeWidth(percent)}
                >
                  {percent}%
                </button>
              )}
            </For>
            <Divider />
            <Tool icon="material-symbols-light:delete-outline" label="Eliminar imagen" onClick={images.remove} />
          </div>
        </>
      )}
    </Show>
  )

  /** Mini-barra que aparece sobre el texto seleccionado. */
  const FloatingBar = () => (
    <Show when={floating()}>
      {pos => (
        <div
          class="absolute z-40 flex -translate-x-1/2 items-center gap-0.5 rounded-lg border border-border bg-surface px-1 py-0.5 shadow-lg"
          style={{ left: `${pos().left}px`, top: `${pos().top}px` }}
        >
          <Tool icon="material-symbols-light:format-bold" label="Negrita" active={format().bold} onClick={() => run('bold')} />
          <Tool icon="material-symbols-light:format-italic" label="Cursiva" active={format().italic} onClick={() => run('italic')} />
          <Tool icon="material-symbols-light:format-underlined" label="Subrayado" active={format().underline} onClick={() => run('underline')} />
          <Tool icon="material-symbols-light:link" label="Enlace" active={format().link} onClick={openLinkDialog} />
          <Tool icon="material-symbols-light:format-clear" label="Limpiar formato" onClick={() => run('removeFormat')} />
        </div>
      )}
    </Show>
  )

  const HtmlView = () => (
    <div class="p-3">
      <Textarea
        value={html()}
        onInput={(e: InputEvent & { currentTarget: HTMLTextAreaElement }) => {
          const safe = sanitizeHtml(e.currentTarget.value)
          setHtml(safe)
          local.onChange?.(safe)
        }}
        class="min-h-50 font-mono text-sm"
        disabled={local.disabled}
        placeholder="HTML del contenido"
      />
    </div>
  )

  const EditableView = () => (
    <div class="relative">
      <Show when={isEmpty()}>
        <div class="pointer-events-none absolute left-4 top-3 select-none text-sm text-foreground-subtle/70">{local.placeholder || 'Escribe aquí...'}</div>
      </Show>
      <div
        ref={editorRef}
        role="textbox"
        aria-multiline="true"
        tabindex={0}
        class={twMerge(
          'w-full p-4 outline-none',
          'prose prose-slate max-w-none text-foreground leading-relaxed dark:prose-invert',
          'prose-p:my-2 prose-headings:my-3 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5',
          'prose-blockquote:border-l-4 prose-blockquote:border-primary/40 prose-blockquote:pl-4 prose-blockquote:italic',
          'prose-img:my-2 prose-a:text-primary',
          cssLength(local.minHeight) ? '' : 'min-h-50',
          cssClass(local.minHeight)
        )}
        contentEditable={!local.disabled}
        onInput={() => {
          emit()
          readFormat()
        }}
        onKeyDown={handleKeyDown}
        onKeyUp={readFormat}
        onMouseUp={() => {
          readFormat()
          updateFloating()
        }}
        onPaste={handlePaste}
        onDrop={handleDrop}
        onClick={handleClick}
        onScroll={images.measure}
        style={{ 'caret-color': 'rgb(var(--color-primary-500))', 'word-wrap': 'break-word', 'min-height': cssLength(local.minHeight) }}
      />
      <ImageOverlay />
      <FloatingBar />
    </div>
  )

  const Footer = () => (
    <div class="flex shrink-0 items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-xs text-foreground-subtle">
      <Show when={dictation.listening()} fallback={<span>{format().link ? 'Enlace seleccionado' : ''}</span>}>
        <span class="flex items-center gap-1.5 text-danger">
          <span class="size-2 animate-pulse rounded-full bg-danger" />
          Escuchando{dictation.interim() ? `: ${dictation.interim()}` : '...'}
        </span>
      </Show>
      <span>
        {counter().words} palabras · {counter().chars} caracteres
      </span>
    </div>
  )

  const Body = () => (
    // El alto se limita con `style`, no con clases: `maxHeight` se usa con
    // valores CSS ("200px", "40vh") y como clase Tailwind, y al pasarlo siempre
    // como clase el límite no se aplicaba — el editor crecía sin fin al escribir.
    <div class={twMerge('flex-1 overflow-y-auto', cssClass(local.maxHeight))} style={isFullscreen() ? undefined : { 'max-height': cssLength(local.maxHeight) ?? '60vh' }}>
      <Show when={!isHtmlMode()} fallback={<HtmlView />}>
        <EditableView />
      </Show>
    </div>
  )

  return (
    // En pantalla completa el editor tapa la página entera (cabeceras sticky
    // incluidas, que llegan a z-50) pero queda por DEBAJO de los overlays de la
    // librería —modal 1000, toast 1100, tooltip 1200, selector de color 1300—.
    // Con el z-9999 de antes los tapaba, y los diálogos de imagen, enlace y
    // color parecían no abrirse.
    <div {...(others as JSX.HTMLAttributes<HTMLDivElement>)} class={twMerge(local.class, isFullscreen() ? 'fixed inset-0 z-[900] flex h-screen! flex-col bg-surface' : '')}>
      {/* Un único árbol para las dos vistas: sólo cambian las clases.
          Con dos ramas (una para pantalla completa y otra para la normal) el
          `div` editable se destruía y volvía a crearse vacío al cambiar, y el
          contenido escrito se perdía. */}
      <Card variant="outlined" class={twMerge('flex flex-col overflow-hidden', isFullscreen() ? 'h-screen rounded-none border-0' : 'h-full')}>
        <Toolbar />
        <Body />
        <Footer />
      </Card>

      <LinkDialog isOpen={linkOpen()} initial={linkInitial()} editing={editingLink()} onClose={() => setLinkOpen(false)} onSubmit={submitLink} onRemove={removeLink} />
      <ImageDialog isOpen={imageOpen()} onClose={() => setImageOpen(false)} onSubmit={insertImageValue} onError={message => toast.addToast({ title: message, type: 'warning' })} />

      <Show when={local.name}>
        <input ref={hiddenRef} type="hidden" name={local.name} value={html()} />
      </Show>

      <FieldMessage id={`${fieldId}-message`} error={resolvedError()} helperText={local.helperText} indent={false} />
    </div>
  )
}

export default EditorInput
